import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import {
  AWAssetDto,
  AWStageMemberDto,
  AWTemplateDto,
  CreateAWDto,
  CreateAWStageDto,
  CreateAWStageMemberDto,
  PictureDto
} from '@api/Api';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { useAuth } from '@hooks/useAuth';
import { ApprovalModalStateProps } from '@redux/types/modalType';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useGetApprovalWorkflow } from '@hooks/queries/approval';

import { formattedAWAsset } from '@components/Modals/ApprovalModal/ApprovalModal/utils';

import Steps from './components/Steps';
import type { StepItemKey } from './components/Steps/Steps';
import CreatingApprovalStep from './components/CreatingApprovalStep';
import MediaStep from './components/MediaStep';
import StagesStep from './components/StagesStep';
import SettingsStep from './components/SettingsStep';
import FinalReviewStep from './components/FinalReviewStep';

import './ApprovalModal.less';

const ApprovalFallbackModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/ApprovalFallbackModal')
);

export type AwModeState =
  | 'create'
  | 'edit'
  | 'aw-split-copy'
  | 'edit-template'
  | 'create-template';

export type AwStateStageMember = CreateAWStageMemberDto & {
  name?: string | null;
  picture: PictureDto;
  me?: boolean | null;
  organizationRole?: AWStageMemberDto['organizationRole'];
};

export type AwStateStage = Omit<CreateAWStageDto, 'members'> & {
  id: string;
  deadline?: string | null;
  isFinished?: boolean;
  members: AwStateStageMember[];
};

export type AwState = Omit<CreateAWDto, 'assetIds' | 'stages'> & {
  name: string;
  stages: AwStateStage[];
  lockDecisions: boolean;
  disableNonApprovedDownloads: boolean;
  password: string | null;
  customMessage: string | null;
  allowAllVersions: boolean;
  allowAllComments: boolean;
  restart?: boolean;
};

export default function ApprovalModal(props: ApprovalModalStateProps) {
  const { visible, campaignId, folderId, callback } = props;
  const dispatch = useDispatch();
  const { user } = useAuth(true);
  const assetVersions =
    'assetVersions' in props ? props.assetVersions : undefined;
  const openStep = 'openStep' in props ? props.openStep : undefined;
  const approvalWorkflowId =
    'approvalWorkflowId' in props ? props.approvalWorkflowId : undefined;
  const approvalWorkflowSplit =
    'approvalWorkflowSplit' in props ? props.approvalWorkflowSplit : undefined;

  const {
    data,
    isLoading: isLoadingApprovalWorkflow,
    error
  } = useGetApprovalWorkflow({
    enabled: !!approvalWorkflowId,
    params: {
      id: approvalWorkflowId || ''
    }
  });
  const isLoading = isLoadingApprovalWorkflow && !!approvalWorkflowId;

  const approvalWorkflow = data?.approvalWorkflow;
  const ownerEmail = approvalWorkflow?.owner?.user?.email || user.email;

  const [awMode, setAwMode] = useState<AwModeState>(
    approvalWorkflowId ? 'edit' : 'create'
  );
  const [currentStep, setCurrentStep] =
    useState<StepItemKey>('creatingApproval');
  const [completedSteps, setCompletedSteps] = useState<StepItemKey[]>(
    approvalWorkflowId
      ? ['creatingApproval', 'media', 'stages', 'settings', 'finalReview']
      : []
  );

  const [selectedAssetVersions, setSelectedAssetVersions] = useState<
    AWAssetDto[]
  >(() => {
    return (assetVersions || []).map((version) => formattedAWAsset(version));
  });

  const [awState, setAwState] = useState<AwState | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AWTemplateDto | null>(null);

  useEffect(() => {
    if (!approvalWorkflow) return;
    if (awState) return;
    setAwState({
      name: approvalWorkflow.name,
      stages: approvalWorkflow.stages,
      lockDecisions: approvalWorkflow.lockDecisions,
      disableNonApprovedDownloads: approvalWorkflow.disableNonApprovedDownloads,
      password: approvalWorkflow.password || null,
      customMessage: approvalWorkflow.customMessage || null,
      allowAllVersions: approvalWorkflow.allowAllVersions,
      allowAllComments: approvalWorkflow.allowAllComments,
      templateId: approvalWorkflow.templateId,
      newVersionBehavior: approvalWorkflow.newVersionBehavior
    });
    setSelectedAssetVersions(approvalWorkflow.assets);
    setCurrentStep(openStep || 'creatingApproval');
  }, [
    approvalWorkflow,
    awState,
    openStep,
    setAwState,
    setCurrentStep,
    setSelectedAssetVersions
  ]);

  useEffect(() => {
    if (!approvalWorkflowSplit?.assets?.length) return;
    setSelectedAssetVersions(approvalWorkflowSplit.assets);
  }, [approvalWorkflowSplit]);

  const onChangeStep = (step: StepItemKey) => {
    if (currentStep === 'media' && !selectedAssetVersions.length) return;
    if (currentStep === 'stages' && !awState?.stages.length) return;
    if (awMode === 'create-template' && step === 'media') return;
    if (awMode === 'edit-template' && step === 'media') return;
    setCurrentStep(step);
    setCompletedSteps((prev) => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  };

  const sendMixpanelEvent = () => {
    MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
      addToFavorite: false,
      deleteAsset: false,
      newAsset: false,
      externalLinkShare: false,
      approvalRouting: true,
      newCommentAsset: false,
      annotationLeft: false,
      taskFromComment: false
    });
  };

  const onCloseModal = useCallback(() => {
    dispatch(toggleApprovalModal(null));
  }, [dispatch]);

  const onResetModal = () => {
    setCurrentStep('creatingApproval');
    setCompletedSteps([]);
    setSelectedTemplate(null);
    setAwState(null);
  };

  if (
    (!!approvalWorkflow && !approvalWorkflow?.permissions?.manage) ||
    !!error
  ) {
    return <ApprovalFallbackModal visible={visible} onClose={onCloseModal} />;
  }

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={1408}
      destroyOnClose
      zIndex={1010}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={() => onCloseModal()}
      className={classNames('handle-approval-modal')}
      afterClose={() => dispatch(toggleApprovalModal(null))}
    >
      <CrossSvg
        className="handle-approval-modal-btn-close"
        onClick={onCloseModal}
      />
      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {!isLoading && (
        <div className="handle-approval-modal-wrapper">
          <Steps
            currentStep={currentStep}
            awMode={awMode}
            completedSteps={completedSteps}
            onChangeStep={onChangeStep}
          />
          <div className="handle-approval-modal-content">
            {currentStep === 'creatingApproval' && (
              <CreatingApprovalStep
                campaignId={campaignId}
                folderId={folderId}
                selectedTemplate={selectedTemplate}
                awState={awState}
                awMode={awMode}
                approvalWorkflowSplit={approvalWorkflowSplit}
                setAwMode={setAwMode}
                setSelectedTemplate={setSelectedTemplate}
                onCloseModal={onCloseModal}
                onChangeStep={onChangeStep}
                setAwState={setAwState}
              />
            )}
            {!!awState && (
              <>
                {currentStep === 'media' && (
                  <MediaStep
                    approvalRoutingName={awState.name}
                    campaignId={campaignId}
                    approvalWorkflowAssets={approvalWorkflow?.assets || []}
                    selectedAssetVersions={selectedAssetVersions}
                    setSelectedAssetVersions={setSelectedAssetVersions}
                    onChangeStep={onChangeStep}
                    onCloseModal={onCloseModal}
                  />
                )}
                {currentStep === 'stages' && (
                  <StagesStep
                    awState={awState}
                    campaignId={campaignId}
                    ownerEmail={ownerEmail}
                    setAwState={setAwState}
                    onChangeStep={onChangeStep}
                    onCloseModal={onCloseModal}
                  />
                )}
                {currentStep === 'settings' && (
                  <SettingsStep
                    awState={awState}
                    setAwState={setAwState}
                    onChangeStep={onChangeStep}
                    onCloseModal={onCloseModal}
                  />
                )}
                {currentStep === 'finalReview' && (
                  <FinalReviewStep
                    awMode={awMode}
                    awState={awState}
                    ownerEmail={ownerEmail}
                    approvalWorkflow={approvalWorkflow}
                    selectedTemplate={selectedTemplate}
                    selectedAssetVersions={selectedAssetVersions}
                    approvalWorkflowSplit={approvalWorkflowSplit}
                    onChangeStep={onChangeStep}
                    sendMixpanelEvent={sendMixpanelEvent}
                    onCloseModal={onCloseModal}
                    onResetModal={onResetModal}
                    callback={callback}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
